import React, { useMemo, useRef, useState } from 'react';
import {
  ChevronRight,
  Download,
  Eye,
  File,
  Folder,
  RefreshCw,
  Upload,
  X,
} from 'lucide-react';
import { AnimatedLogo } from '@/embedded/chat/components/animated-logo';
import { cn } from '@/utils/cn';
import { Button } from '../ui/button';
import { chatLucideIconProps } from './chat-ui';
import type { WebAgentFileEntry } from '../../lib/chat-api';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function basename(filePath: string): string {
  if (filePath === '/') {
    return '/';
  }

  const parts = filePath.split('/').filter(Boolean);
  return parts[parts.length - 1] ?? filePath;
}

function buildBreadcrumbs(currentPath: string) {
  const parts = currentPath.split('/').filter(Boolean);
  const crumbs = [{ label: '/', path: '/' }];
  let acc = '';
  for (const part of parts) {
    acc += `/${part}`;
    crumbs.push({ label: part, path: acc });
  }
  return crumbs;
}

function VFSLoadingLogo({
  className,
  logoClassName,
  strokeWidth = 2,
}: {
  className?: string;
  logoClassName?: string;
  strokeWidth?: number;
}) {
  return (
    <div className={cn('flex items-center justify-center', className)}>
      <AnimatedLogo
        loop
        strokeWidth={strokeWidth}
        aria-hidden="true"
        className={cn('h-4 w-auto text-current', logoClassName)}
      />
    </div>
  );
}

export interface VFSBrowserProps {
  className?: string;
  currentPath: string;
  entries: WebAgentFileEntry[];
  isLoading: boolean;
  error?: string;
  isUploading: boolean;
  downloadingPath: string | null;
  onNavigate: (path: string) => void;
  onRefresh: () => void;
  onUpload: (files: File[]) => Promise<void> | void;
  onPreview: (path: string) => Promise<void> | void;
  onDownload: (path: string) => Promise<void> | void;
  onClose?: () => void;
}

export function VFSBrowser({
  className,
  currentPath,
  entries,
  isLoading,
  error,
  isUploading,
  downloadingPath,
  onNavigate,
  onRefresh,
  onUpload,
  onPreview,
  onDownload,
  onClose,
}: VFSBrowserProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const sortedEntries = useMemo(
    () =>
      [...entries].sort((left, right) => {
        if (left.kind !== right.kind) {
          return left.kind === 'dir' ? -1 : 1;
        }
        return basename(left.path).localeCompare(basename(right.path));
      }),
    [entries]
  );

  const crumbs = useMemo(() => buildBreadcrumbs(currentPath), [currentPath]);

  const handleFiles = async (fileList: FileList | null) => {
    const files = fileList ? Array.from(fileList) : [];
    if (files.length === 0) {
      return;
    }
    await onUpload(files);
  };

  return (
    <div
      className={cn(
        'flex h-full min-h-0 flex-col border-l border-border/70 bg-muted/20',
        className
      )}
    >
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={async event => {
          await handleFiles(event.target.files);
          event.currentTarget.value = '';
        }}
      />

      <div className="shrink-0 px-4 pb-3 pt-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex min-h-8 flex-wrap items-center gap-1 text-xs text-muted-foreground">
              {crumbs.map((crumb, index) => (
                <React.Fragment key={crumb.path}>
                  {index > 0 ? (
                    <ChevronRight {...chatLucideIconProps} className="size-3" />
                  ) : null}
                  <button
                    type="button"
                    className={cn(
                      'rounded-sm px-1 py-0.5 transition-colors hover:bg-accent hover:text-foreground',
                      crumb.path === currentPath ? 'text-foreground' : undefined
                    )}
                    onClick={() => onNavigate(crumb.path)}
                  >
                    {crumb.label}
                  </button>
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1">
            <Button
              variant="ghost"
              size="icon-sm"
              className="rounded-lg"
              onClick={onRefresh}
              disabled={isLoading || isUploading}
              aria-label="Refresh files"
            >
              <RefreshCw
                {...chatLucideIconProps}
                className={cn('size-4', isLoading ? 'animate-spin' : undefined)}
              />
            </Button>
            {onClose ? (
              <Button
                variant="ghost"
                size="icon-sm"
                className="rounded-lg"
                onClick={onClose}
                aria-label="Close file browser"
              >
                <X {...chatLucideIconProps} className="size-4" />
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      {error ? (
        <div className="mx-4 mb-3 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      <div
        className={cn(
          'vfs-browser-scroll flex-1 overflow-y-auto px-3 py-3 transition-colors',
          isDragOver ? 'bg-primary/5' : undefined
        )}
        onDragOver={event => {
          event.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={event => {
          event.preventDefault();
          if (event.currentTarget.contains(event.relatedTarget as Node)) {
            return;
          }
          setIsDragOver(false);
        }}
        onDrop={async event => {
          event.preventDefault();
          setIsDragOver(false);
          await handleFiles(event.dataTransfer.files);
        }}
      >
        {isLoading ? (
          <div className="flex h-full min-h-[200px] items-center justify-center">
            <div className="flex size-16 items-center justify-center">
              <VFSLoadingLogo
                className="size-full"
                logoClassName="h-10 text-foreground/18"
              />
              <span className="sr-only">Loading files</span>
            </div>
          </div>
        ) : (
          <div className="flex flex-col">
            {sortedEntries.length === 0 ? (
              <div className="flex min-h-[200px] items-center justify-center">
                <div className="chat-type-body-sm max-w-xs rounded-xl bg-background/80 px-5 py-4 text-center text-muted-foreground">
                  Drop files here or upload content for this folder.
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                {sortedEntries.map(entry => {
                  const isDir = entry.kind === 'dir';
                  const isDownloading = downloadingPath === entry.path;
                  return (
                    <div
                      key={entry.path}
                      className="flex items-center justify-between gap-2 rounded-xl px-3 py-2.5 transition-colors hover:bg-background"
                    >
                      <button
                        type="button"
                        className="flex min-w-0 flex-1 items-center gap-2 text-left"
                        onClick={() => {
                          if (isDir) {
                            onNavigate(entry.path);
                          } else {
                            void onPreview(entry.path);
                          }
                        }}
                      >
                        {isDir ? (
                          <Folder
                            {...chatLucideIconProps}
                            className="size-4 shrink-0 text-primary"
                          />
                        ) : (
                          <File
                            {...chatLucideIconProps}
                            className="size-4 shrink-0 text-muted-foreground"
                          />
                        )}
                        <div className="min-w-0">
                          <div className="chat-type-body-sm truncate text-foreground">
                            {basename(entry.path)}
                          </div>
                          <div className="chat-type-caption text-muted-foreground">
                            {isDir ? 'Folder' : formatFileSize(entry.size)}
                          </div>
                        </div>
                      </button>

                      {!isDir ? (
                        <div className="flex shrink-0 items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="rounded-lg"
                            onClick={() => {
                              void onPreview(entry.path);
                            }}
                            aria-label={`Preview ${basename(entry.path)}`}
                          >
                            <Eye {...chatLucideIconProps} className="size-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="rounded-lg"
                            onClick={() => {
                              void onDownload(entry.path);
                            }}
                            disabled={isDownloading}
                            aria-label={`Download ${basename(entry.path)}`}
                          >
                            {isDownloading ? (
                              <VFSLoadingLogo
                                className="size-4"
                                logoClassName="h-3.5 text-muted-foreground"
                              />
                            ) : (
                              <Download
                                {...chatLucideIconProps}
                                className="size-4"
                              />
                            )}
                          </Button>
                        </div>
                      ) : (
                        <ChevronRight
                          {...chatLucideIconProps}
                          className="size-4 shrink-0 text-muted-foreground"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="sticky bottom-0 mt-3 bg-gradient-to-t from-muted/20 via-muted/20 pt-3">
              <Button
                variant="outline"
                size="sm"
                className="w-full rounded-lg bg-background/90"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                {isUploading ? (
                  <VFSLoadingLogo
                    className="size-4"
                    logoClassName="h-3.5 text-foreground/70"
                  />
                ) : (
                  <Upload {...chatLucideIconProps} className="size-4" />
                )}
                Upload files
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
